/* ============================================================================
 * places.js — Google Places (admin-only) helper
 * ----------------------------------------------------------------------------
 * Loads the Google Maps JS API (Places library) with the REFERRER-RESTRICTED
 * admin key and exposes:
 *   - attachAutocomplete(input, onPlace)  business-name autocomplete → autofill
 *   - findPlace(query)                    text→place (for import geocoding)
 *
 * Compliance: used ONLY inside the admin dashboard, at add/import time. The
 * public embed never loads this. We read place_id / address / geometry / name
 * only — we never request or store opening_hours or Places phone content.
 *
 * Key: process.env.REACT_APP_GOOGLE_PLACES_KEY (referrer-restricted, Places +
 * Maps JS enabled). If absent, isPlacesConfigured() is false and callers fall
 * back to manual lat/lng entry.
 * ==========================================================================*/

const KEY = process.env.REACT_APP_GOOGLE_PLACES_KEY

let loadPromise = null

export function isPlacesConfigured() {
  return !!KEY
}

// Load the Maps JS API once (singleton). Resolves with window.google.
export function loadGoogleMaps() {
  if (!KEY) return Promise.reject(new Error('REACT_APP_GOOGLE_PLACES_KEY not set'))
  if (window.google?.maps?.places) return Promise.resolve(window.google)
  if (loadPromise) return loadPromise

  loadPromise = new Promise((resolve, reject) => {
    const cbName = '__shwPlacesReady'
    window[cbName] = () => resolve(window.google)
    const s = document.createElement('script')
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(KEY)}` +
            `&libraries=places&loading=async&callback=${cbName}`
    s.async = true
    s.defer = true
    s.onerror = () => reject(new Error('Failed to load Google Maps JS'))
    document.head.appendChild(s)
  })
  return loadPromise
}

// Pull the fields we store out of a Places result (legacy PlaceResult shape).
export function parsePlace(place) {
  if (!place) return null
  const comp = place.address_components || []
  const get = (type) => comp.find(c => (c.types || []).includes(type))?.long_name || ''
  const loc = place.geometry?.location
  return {
    name:     place.name || '',
    place_id: place.place_id || null,
    address:  place.formatted_address || '',
    suburb:   get('locality') || get('postal_town') || get('sublocality') || '',
    lat:      loc ? (typeof loc.lat === 'function' ? loc.lat() : loc.lat) : null,
    lng:      loc ? (typeof loc.lng === 'function' ? loc.lng() : loc.lng) : null,
  }
}

// Bind an autocomplete widget to an <input>. Calls onPlace(parsed) on select.
// Returns a cleanup function.
export async function attachAutocomplete(input, onPlace) {
  const google = await loadGoogleMaps()
  const ac = new google.maps.places.Autocomplete(input, {
    componentRestrictions: { country: 'au' },
    fields: ['place_id', 'name', 'formatted_address', 'geometry', 'address_components'],
    types: ['establishment'],
  })
  const listener = ac.addListener('place_changed', () => {
    const parsed = parsePlace(ac.getPlace())
    if (parsed) onPlace(parsed)
  })
  return () => {
    listener?.remove?.()
    // Remove the stray .pac-container dropdowns Google appends to <body>.
    document.querySelectorAll('.pac-container').forEach(el => el.remove())
  }
}

// Text query → single best place (used to geocode imported new stockists).
export async function findPlace(query) {
  const google = await loadGoogleMaps()
  const svc = new google.maps.places.PlacesService(document.createElement('div'))
  return new Promise((resolve) => {
    svc.findPlaceFromQuery(
      {
        query,
        fields: ['place_id', 'name', 'formatted_address', 'geometry'],
        locationBias: new google.maps.LatLngBounds(
          new google.maps.LatLng(-35.2, 112.9),
          new google.maps.LatLng(-13.7, 129.0),
        ),
      },
      (results, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && results?.[0]) {
          // findPlaceFromQuery omits address_components; fetch details for suburb.
          svc.getDetails(
            { placeId: results[0].place_id, fields: ['place_id', 'name', 'formatted_address', 'geometry', 'address_components'] },
            (detail, dStatus) => {
              if (dStatus === google.maps.places.PlacesServiceStatus.OK && detail) resolve(parsePlace(detail))
              else resolve(parsePlace(results[0]))
            },
          )
        } else resolve(null)
      },
    )
  })
}
